import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Languages, HardHat, LayoutDashboard, LogOut, Shield, Menu } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggle, logo } = useLang();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const onLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const AuthActions = ({ onNavigate = () => {}, stacked = false }) => (
    user && user.role ? (
      <>
        {user.role === "admin" && (
          <Button asChild variant="ghost" size="sm" className={`rounded-sm ${stacked ? "w-full justify-start" : ""}`}>
            <Link to="/admin" onClick={onNavigate} data-testid="nav-admin-link"><Shield className="mr-1 h-4 w-4" />{t("admin")}</Link>
          </Button>
        )}
        {(user.role === "business" || user.role === "admin") && (
          <Button asChild variant="ghost" size="sm" className={`rounded-sm ${stacked ? "w-full justify-start" : ""}`}>
            <Link to="/dashboard" onClick={onNavigate} data-testid="nav-dashboard-link"><LayoutDashboard className="mr-1 h-4 w-4" />{t("nav_dashboard")}</Link>
          </Button>
        )}
        <Button onClick={onLogout} variant="outline" size="sm" className={`rounded-sm ${stacked ? "w-full justify-start" : ""}`} data-testid="nav-logout-btn">
          <LogOut className="mr-1 h-4 w-4" />{t("nav_logout")}
        </Button>
      </>
    ) : (
      <>
        <Button asChild variant="ghost" size="sm" className={`rounded-sm ${stacked ? "w-full justify-start" : ""}`}>
          <Link to="/login" onClick={onNavigate} data-testid="nav-login-link">{t("nav_login")}</Link>
        </Button>
        <Button asChild size="sm" className={`rounded-sm bg-primary hover:bg-orange-600 ${stacked ? "w-full justify-start" : ""}`}>
          <Link to="/register" onClick={onNavigate} data-testid="nav-register-link">{t("nav_register")}</Link>
        </Button>
      </>
    )
  );

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-brand">
          {logo ? (
            <img src={logo} alt={t("brand")} className="h-14 w-auto max-w-[220px] object-contain sm:h-16" data-testid="navbar-logo" />
          ) : (
            <>
              <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-primary text-white">
                <HardHat className="h-6 w-6" />
              </span>
              <span className="hidden font-head text-xl font-extrabold tracking-tight text-secondary sm:inline">{t("brand")}</span>
            </>
          )}
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <Link to="/directory" className="text-sm font-semibold text-slate-600 transition-colors hover:text-primary" data-testid="nav-directory-link">{t("nav_directory")}</Link>
          <Link to="/pricing" className="text-sm font-semibold text-slate-600 transition-colors hover:text-primary" data-testid="nav-pricing-link">{t("nav_pricing")}</Link>
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={toggle} className="flex items-center gap-1 rounded-sm border border-slate-200 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 transition-colors hover:border-primary hover:text-primary" data-testid="lang-toggle-btn">
            <Languages className="h-3.5 w-3.5" />
            {lang === "es" ? "EN" : "ES"}
          </button>

          <div className="hidden items-center gap-2 md:flex">
            <AuthActions />
          </div>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="flex items-center justify-center rounded-sm border border-slate-200 p-2 text-slate-600 transition-colors hover:border-primary hover:text-primary md:hidden" data-testid="mobile-menu-btn" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72" data-testid="mobile-menu-panel">
              <nav className="mt-8 flex flex-col gap-1">
                <Link to="/directory" onClick={() => setMenuOpen(false)} className="rounded-sm px-3 py-2.5 text-base font-semibold text-secondary transition-colors hover:bg-slate-100" data-testid="nav-directory-link-mobile">{t("nav_directory")}</Link>
                <Link to="/pricing" onClick={() => setMenuOpen(false)} className="rounded-sm px-3 py-2.5 text-base font-semibold text-secondary transition-colors hover:bg-slate-100" data-testid="nav-pricing-link-mobile">{t("nav_pricing")}</Link>
              </nav>
              <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4">
                <AuthActions onNavigate={() => setMenuOpen(false)} stacked />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
